import { useEffect, useState } from 'react';
import '../styles/FlujoCaja.css';

const FlujoCaja = () => {
  const [datosFlujoCaja, setDatosFlujoCaja] = useState(null);
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState('mes_actual');
  const [proyeccion, setProyeccion] = useState(null);
  const [comparativo, setComparativo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Datos simulados - reemplazar con llamadas API reales
  const datosSimulados = {
    mes_actual: {
      periodo: 'Noviembre 2024',
      saldo_inicial: 850000,
      total_ingresos: 2450000,
      total_egresos: 1890000,
      saldo_final: 1410000,
      flujo_neto: 560000,
      desglose_ingresos: {
        pagos_prestamos: 1850000,
        intereses: 320000,
        comisiones: 180000,
        otros: 100000
      },
      desglose_egresos: {
        gastos_operativos: 450000,
        nomina: 620000,
        prestamos_dados: 650000,
        otros_gastos: 170000
      },
      flujo_diario: [
        { fecha: '01/11', ingresos: 85000, egresos: 65000, saldo: 870000 },
        { fecha: '02/11', ingresos: 95000, egresos: 72000, saldo: 893000 },
        { fecha: '03/11', ingresos: 78000, egresos: 55000, saldo: 916000 },
        { fecha: '04/11', ingresos: 120000, egresos: 89000, saldo: 947000 },
        { fecha: '05/11', ingresos: 105000, egresos: 68000, saldo: 984000 },
        { fecha: '06/11', ingresos: 92000, egresos: 74000, saldo: 1002000 },
        { fecha: '07/11', ingresos: 88000, egresos: 62000, saldo: 1028000 }
      ]
    },
    semana_actual: {
      periodo: 'Semana 4 Nov 2024',
      saldo_inicial: 1250000,
      total_ingresos: 650000,
      total_egresos: 480000,
      saldo_final: 1420000,
      flujo_neto: 170000,
      desglose_ingresos: {
        pagos_prestamos: 485000,
        intereses: 85000,
        comisiones: 55000,
        otros: 25000
      },
      desglose_egresos: {
        gastos_operativos: 120000,
        nomina: 155000,
        prestamos_dados: 150000,
        otros_gastos: 55000
      }
    }
  };

  const proyeccionSimulada = {
    proxima_semana: {
      ingresos_estimados: 580000,
      egresos_estimados: 420000,
      flujo_neto_estimado: 160000,
      saldo_proyectado: 1580000
    },
    proximo_mes: {
      ingresos_estimados: 2350000,
      egresos_estimados: 1980000,
      flujo_neto_estimado: 370000,
      saldo_proyectado: 1790000
    }
  };

  const comparativoSimulado = {
    vs_mes_anterior: {
      periodo_actual: 'Noviembre 2024',
      periodo_anterior: 'Octubre 2024',
      variacion_ingresos: 8.5,
      variacion_egresos: -3.2,
      variacion_flujo_neto: 15.7,
      ingresos_anterior: 2260000,
      egresos_anterior: 1952000,
      flujo_neto_anterior: 308000
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [periodoSeleccionado]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      // Simular llamada API
      await new Promise(resolve => setTimeout(resolve, 1000));

      setDatosFlujoCaja(datosSimulados[periodoSeleccionado]);
      setProyeccion(proyeccionSimulada);
      setComparativo(comparativoSimulado);
      setError(null);
    } catch (err) {
      setError('Error al cargar los datos del flujo de caja');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 0
    }).format(monto);
  };

  const obtenerClaseVariacion = (variacion) => {
    if (variacion > 0) return 'positiva';
    if (variacion < 0) return 'negativa';
    return 'neutra';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Cargando datos del flujo de caja...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={cargarDatos} className="btn-retry">
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="flujo-caja-container">
      {/* Header */}
      <div className="flujo-header">
        <h1>💰 Flujo de Caja</h1>
        <p>Análisis detallado de ingresos y egresos por período</p>
      </div>

      {/* Selector de período */}
      <div className="periodo-selector-section">
        <div className="selector-container">
          <label htmlFor="periodo">Período de análisis:</label>
          <select
            id="periodo"
            value={periodoSeleccionado}
            onChange={(e) => setPeriodoSeleccionado(e.target.value)}
            className="periodo-select"
          >
            <option value="mes_actual">Mes Actual</option>
            <option value="semana_actual">Semana Actual</option>
            <option value="trimestre_actual">Trimestre Actual</option>
            <option value="año_actual">Año Actual</option>
          </select>
        </div>
      </div>

      {/* Resumen principal */}
      <div className="resumen-principal-section">
        <h2>Resumen del Período: {datosFlujoCaja.periodo}</h2>

        <div className="resumen-cards">
          <div className="flujo-card saldo-inicial">
            <div className="card-icon">💵</div>
            <div className="card-content">
              <h3>Saldo Inicial</h3>
              <div className="card-value">{formatearMoneda(datosFlujoCaja.saldo_inicial)}</div>
            </div>
          </div>

          <div className="flujo-card total-ingresos">
            <div className="card-icon">📈</div>
            <div className="card-content">
              <h3>Total Ingresos</h3>
              <div className="card-value">{formatearMoneda(datosFlujoCaja.total_ingresos)}</div>
            </div>
          </div>

          <div className="flujo-card total-egresos">
            <div className="card-icon">📉</div>
            <div className="card-content">
              <h3>Total Egresos</h3>
              <div className="card-value">{formatearMoneda(datosFlujoCaja.total_egresos)}</div>
            </div>
          </div>

          <div className="flujo-card flujo-neto">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <h3>Flujo Neto</h3>
              <div className={`card-value ${datosFlujoCaja.flujo_neto >= 0 ? 'positivo' : 'negativo'}`}>
                {formatearMoneda(datosFlujoCaja.flujo_neto)}
              </div>
            </div>
          </div>

          <div className="flujo-card saldo-final">
            <div className="card-icon">🏦</div>
            <div className="card-content">
              <h3>Saldo Final</h3>
              <div className="card-value">{formatearMoneda(datosFlujoCaja.saldo_final)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Desglose detallado */}
      <div className="desglose-section">
        <h2>Desglose Detallado</h2>

        <div className="desglose-grid">
          {/* Ingresos */}
          <div className="desglose-columna ingresos">
            <h3>📈 Ingresos Detallados</h3>
            <div className="desglose-items">
              <div className="desglose-item">
                <span className="concepto">Pagos de Préstamos</span>
                <span className="monto">{formatearMoneda(datosFlujoCaja.desglose_ingresos.pagos_prestamos)}</span>
              </div>
              <div className="desglose-item">
                <span className="concepto">Intereses</span>
                <span className="monto">{formatearMoneda(datosFlujoCaja.desglose_ingresos.intereses)}</span>
              </div>
              <div className="desglose-item">
                <span className="concepto">Comisiones</span>
                <span className="monto">{formatearMoneda(datosFlujoCaja.desglose_ingresos.comisiones)}</span>
              </div>
              <div className="desglose-item">
                <span className="concepto">Otros Ingresos</span>
                <span className="monto">{formatearMoneda(datosFlujoCaja.desglose_ingresos.otros)}</span>
              </div>
            </div>
            <div className="desglose-total">
              <span>Total Ingresos:</span>
              <span>{formatearMoneda(datosFlujoCaja.total_ingresos)}</span>
            </div>
          </div>

          {/* Egresos */}
          <div className="desglose-columna egresos">
            <h3>📉 Egresos Detallados</h3>
            <div className="desglose-items">
              <div className="desglose-item">
                <span className="concepto">Gastos Operativos</span>
                <span className="monto">{formatearMoneda(datosFlujoCaja.desglose_egresos.gastos_operativos)}</span>
              </div>
              <div className="desglose-item">
                <span className="concepto">Nómina</span>
                <span className="monto">{formatearMoneda(datosFlujoCaja.desglose_egresos.nomina)}</span>
              </div>
              <div className="desglose-item">
                <span className="concepto">Préstamos Otorgados</span>
                <span className="monto">{formatearMoneda(datosFlujoCaja.desglose_egresos.prestamos_dados)}</span>
              </div>
              <div className="desglose-item">
                <span className="concepto">Otros Gastos</span>
                <span className="monto">{formatearMoneda(datosFlujoCaja.desglose_egresos.otros_gastos)}</span>
              </div>
            </div>
            <div className="desglose-total">
              <span>Total Egresos:</span>
              <span>{formatearMoneda(datosFlujoCaja.total_egresos)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Flujo diario (solo para mes actual) */}
      {periodoSeleccionado === 'mes_actual' && datosFlujoCaja.flujo_diario && (
        <div className="flujo-diario-section">
          <h2>Flujo Diario (Últimos 7 días)</h2>

          <div className="grafico-flujo">
            <div className="grafico-barras-flujo">
              {datosFlujoCaja.flujo_diario.map((dia, index) => {
                const maxSaldo = Math.max(...datosFlujoCaja.flujo_diario.map(d => d.saldo));
                const alturaBarra = (dia.saldo / maxSaldo) * 100;

                return (
                  <div key={index} className="dia-flujo">
                    <div className="dia-info">
                      <div className="dia-fecha">{dia.fecha}</div>
                      <div className="dia-ingresos">+{formatearMoneda(dia.ingresos)}</div>
                      <div className="dia-egresos">-{formatearMoneda(dia.egresos)}</div>
                    </div>
                    <div
                      className="barra-flujo"
                      style={{ height: `${alturaBarra}%` }}
                      title={`Saldo: ${formatearMoneda(dia.saldo)}`}
                    />
                    <div className="dia-saldo">{formatearMoneda(dia.saldo)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Proyección */}
      <div className="proyeccion-section">
        <h2>📊 Proyección Futura</h2>

        <div className="proyeccion-cards">
          <div className="proyeccion-card">
            <h3>🗓️ Próxima Semana</h3>
            <div className="proyeccion-detalles">
              <div className="proyeccion-item">
                <span>Ingresos Estimados:</span>
                <span className="positivo">{formatearMoneda(proyeccion.proxima_semana.ingresos_estimados)}</span>
              </div>
              <div className="proyeccion-item">
                <span>Egresos Estimados:</span>
                <span className="negativo">{formatearMoneda(proyeccion.proxima_semana.egresos_estimados)}</span>
              </div>
              <div className="proyeccion-item flujo-neto-proyectado">
                <span>Flujo Neto Estimado:</span>
                <span className={proyeccion.proxima_semana.flujo_neto_estimado >= 0 ? 'positivo' : 'negativo'}>
                  {formatearMoneda(proyeccion.proxima_semana.flujo_neto_estimado)}
                </span>
              </div>
              <div className="proyeccion-item saldo-proyectado">
                <span>Saldo Proyectado:</span>
                <span className="destacado">{formatearMoneda(proyeccion.proxima_semana.saldo_proyectado)}</span>
              </div>
            </div>
          </div>

          <div className="proyeccion-card">
            <h3>📅 Próximo Mes</h3>
            <div className="proyeccion-detalles">
              <div className="proyeccion-item">
                <span>Ingresos Estimados:</span>
                <span className="positivo">{formatearMoneda(proyeccion.proximo_mes.ingresos_estimados)}</span>
              </div>
              <div className="proyeccion-item">
                <span>Egresos Estimados:</span>
                <span className="negativo">{formatearMoneda(proyeccion.proximo_mes.egresos_estimados)}</span>
              </div>
              <div className="proyeccion-item flujo-neto-proyectado">
                <span>Flujo Neto Estimado:</span>
                <span className={proyeccion.proximo_mes.flujo_neto_estimado >= 0 ? 'positivo' : 'negativo'}>
                  {formatearMoneda(proyeccion.proximo_mes.flujo_neto_estimado)}
                </span>
              </div>
              <div className="proyeccion-item saldo-proyectado">
                <span>Saldo Proyectado:</span>
                <span className="destacado">{formatearMoneda(proyeccion.proximo_mes.saldo_proyectado)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparativo */}
      <div className="comparativo-section">
        <h2>📈 Comparativo vs Período Anterior</h2>

        <div className="comparativo-card">
          <div className="comparativo-header">
            <h3>{comparativo.vs_mes_anterior.periodo_actual} vs {comparativo.vs_mes_anterior.periodo_anterior}</h3>
          </div>

          <div className="comparativo-grid">
            <div className="comparativo-item">
              <div className="comparativo-concepto">
                <span className="concepto-label">Ingresos</span>
                <div className="comparativo-valores">
                  <span className="valor-actual">{formatearMoneda(datosFlujoCaja.total_ingresos)}</span>
                  <span className="valor-anterior">vs {formatearMoneda(comparativo.vs_mes_anterior.ingresos_anterior)}</span>
                </div>
              </div>
              <div className={`variacion ${obtenerClaseVariacion(comparativo.vs_mes_anterior.variacion_ingresos)}`}>
                {comparativo.vs_mes_anterior.variacion_ingresos > 0 ? '↗️' : comparativo.vs_mes_anterior.variacion_ingresos < 0 ? '↘️' : '➡️'}
                {Math.abs(comparativo.vs_mes_anterior.variacion_ingresos).toFixed(1)}%
              </div>
            </div>

            <div className="comparativo-item">
              <div className="comparativo-concepto">
                <span className="concepto-label">Egresos</span>
                <div className="comparativo-valores">
                  <span className="valor-actual">{formatearMoneda(datosFlujoCaja.total_egresos)}</span>
                  <span className="valor-anterior">vs {formatearMoneda(comparativo.vs_mes_anterior.egresos_anterior)}</span>
                </div>
              </div>
              <div className={`variacion ${obtenerClaseVariacion(-comparativo.vs_mes_anterior.variacion_egresos)}`}>
                {comparativo.vs_mes_anterior.variacion_egresos > 0 ? '↗️' : comparativo.vs_mes_anterior.variacion_egresos < 0 ? '↘️' : '➡️'}
                {Math.abs(comparativo.vs_mes_anterior.variacion_egresos).toFixed(1)}%
              </div>
            </div>

            <div className="comparativo-item">
              <div className="comparativo-concepto">
                <span className="concepto-label">Flujo Neto</span>
                <div className="comparativo-valores">
                  <span className="valor-actual">{formatearMoneda(datosFlujoCaja.flujo_neto)}</span>
                  <span className="valor-anterior">vs {formatearMoneda(comparativo.vs_mes_anterior.flujo_neto_anterior)}</span>
                </div>
              </div>
              <div className={`variacion ${obtenerClaseVariacion(comparativo.vs_mes_anterior.variacion_flujo_neto)}`}>
                {comparativo.vs_mes_anterior.variacion_flujo_neto > 0 ? '↗️' : comparativo.vs_mes_anterior.variacion_flujo_neto < 0 ? '↘️' : '➡️'}
                {Math.abs(comparativo.vs_mes_anterior.variacion_flujo_neto).toFixed(1)}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Indicadores clave */}
      <div className="indicadores-section">
        <h2>📊 Indicadores Clave</h2>

        <div className="indicadores-grid">
          <div className="indicador-card">
            <div className="indicador-icon">💹</div>
            <div className="indicador-info">
              <h4>Ratio de Liquidez</h4>
              <div className="indicador-valor">
                {((datosFlujoCaja.total_ingresos / datosFlujoCaja.total_egresos) * 100).toFixed(1)}%
              </div>
              <div className="indicador-descripcion">
                Ingresos vs Egresos
              </div>
            </div>
          </div>

          <div className="indicador-card">
            <div className="indicador-icon">📈</div>
            <div className="indicador-info">
              <h4>Crecimiento</h4>
              <div className="indicador-valor positivo">
                +{comparativo.vs_mes_anterior.variacion_flujo_neto.toFixed(1)}%
              </div>
              <div className="indicador-descripcion">
                vs mes anterior
              </div>
            </div>
          </div>

          <div className="indicador-card">
            <div className="indicador-icon">🎯</div>
            <div className="indicador-info">
              <h4>Eficiencia</h4>
              <div className="indicador-valor">
                {((datosFlujoCaja.flujo_neto / datosFlujoCaja.total_ingresos) * 100).toFixed(1)}%
              </div>
              <div className="indicador-descripcion">
                Flujo neto / Ingresos
              </div>
            </div>
          </div>

          <div className="indicador-card">
            <div className="indicador-icon">⚡</div>
            <div className="indicador-info">
              <h4>Rotación Diaria</h4>
              <div className="indicador-valor">
                {formatearMoneda(datosFlujoCaja.total_ingresos / 30)}
              </div>
              <div className="indicador-descripcion">
                Promedio por día
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlujoCaja;